/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addAttachment,
  fetchAttachments,
  resetStateAttachments,
  updateAttachment,
} from "../../app/slices/attachmentsSlice";
import { showSnackbar } from "../../app/slices/commonSlice";
import { dateFormat, getBase64, isEmpty } from "../../app/utils";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import ErrorContainer from "../../components/errorContainer/errorContainer";
import FileInput from "../../components/fileInput/fileInput";
import LoadingContainer from "../../components/loadingContainer/loadingContainer";
import "./AttachmentsContainer.css";
import ViewAttachmentDialog from "./ViewAttachmentDialog";

const AttachmentsContainer = ({
  readOnly = false,
  reload = false,
  attachmentTypes = [],
  sourceTable = "",
  recordId,
}) => {
  const {
    list,
    status: statusGet,
    error: errorGet,
  } = useSelector(({ attachments }) => attachments.get);
  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ attachments }) => attachments.put);
  const {
    data: dataPost,
    status: statusPost,
    error: errorPost,
  } = useSelector(({ attachments }) => attachments.post);
  const { formValues } = useSelector(({ applications }) => applications);
  const [localFormValues, setLocalFormValues] = useState({});
  const [isOpenAttachmentView, setIsOpenAttachmentView] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [renderForm, setRenderForm] = useState(true);
  const dispatch = useDispatch();

  //use effects
  useEffect(() => {
    dispatch(resetStateAttachments());
    loadAttachments();
    return () => {
      dispatch(resetStateAttachments());
    };
  }, [recordId, reload]);

  useEffect(() => {
    if (dataPost) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Attachment added successfully",
          type: "success",
        })
      );
      dispatch(resetStateAttachments(["post"]));
      setLocalFormValues({
        documentType: "-- Select One --",
      });
      setRenderForm(false);
      setTimeout(() => {
        setRenderForm(true);
        loadAttachments();
      }, 1000);
    }
  }, [dataPost]);

  useEffect(() => {
    if (dataPut) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Attachment removed successfully",
          type: "success",
        })
      );
      setIsOpenConfirm(false);
      dispatch(resetStateAttachments(["put"]));
      setTimeout(() => {
        loadAttachments();
      }, 1000);
    }
  }, [dataPut]);

  useEffect(() => {
    if (errorPost || errorPut) {
      dispatch(
        showSnackbar({
          open: true,
          message: errorPost?.message || errorPut?.message,
          type: "error",
        })
      );
    }
  }, [errorPost, errorPut]);

  useEffect(() => {
    setLocalFormValues({
      ...localFormValues,
      attachment: null,
    });
  }, [localFormValues?.documentType]);

  //methods
  const loadAttachments = () => {
    dispatch(
      fetchAttachments({
        RECORD_ID: recordId,
        STATUS: "A",
        SOURCE_TABLE: sourceTable,
      })
    );
  };

  const handleLocalChange = (e) => {
    let inputName = e?.target.name;
    let inputValue = e?.target.value;

    setLocalFormValues({
      ...localFormValues,
      [inputName]: inputValue,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      //validate size
      if (e.target.files[0]?.size > 5000000) {
        dispatch(
          showSnackbar({
            open: true,
            type: "error",
            message: "Attachment should not exceed 5 megabytes in size",
          })
        );
        return;
      }
    }

    setLocalFormValues({ ...localFormValues, attachment: e.target.files[0] });
  };

  const isUploading = () => {
    return statusPost === "loading";
  };

  const isUpdating = () => {
    return statusPut === "loading";
  };

  const handleUpload = async () => {
    if (
      !isEmpty(localFormValues?.documentType) &&
      !isEmpty(localFormValues?.attachment) &&
      (localFormValues?.documentType === "Other"
        ? !isEmpty(localFormValues?.otherDocumentType)
        : true)
    ) {
      await getBase64(localFormValues?.attachment, (result) => {
        let _base64 = result;

        dispatch(
          addAttachment({
            DATA: _base64,
            TYPE:
              localFormValues?.documentType !== "Other"
                ? localFormValues?.documentType
                : `[Other] ${localFormValues?.otherDocumentType}`,
            RECORD_ID: parseInt(recordId),
            SOURCE_TABLE: sourceTable,
          })
        );
      });
    }
  };

  const handleRemoveAttachment = () => {
    if (selectedAttachment) {
      dispatch(
        updateAttachment({
          id: selectedAttachment?.ATTACHMENT_ID,
          payload: {
            STATUS: "inactive",
          },
        })
      );
    }
  };

  //build
  const BuildForm = () => {
    return (
      <>
        {renderForm && !readOnly && (
          <Grid container>
            <Grid md={12}>
              <h5 className="mb-5">Add Attachment</h5>
            </Grid>
            <Grid item md={5} xs={12} className="padded-grid-item">
              {localFormValues?.documentType !== "Other" && (
                <TextField
                  key={"input-document-type"}
                  required
                  fullWidth
                  select
                  variant="outlined"
                  name="documentType"
                  id="input-document-type"
                  label="Document Type"
                  onChange={handleLocalChange}
                  value={localFormValues?.documentType}
                >
                  {[
                    { name: "-- Select One --", extension: "-" },
                    ...attachmentTypes,
                  ].map((option) => {
                    return (
                      <MenuItem
                        key={option?.name || option}
                        value={option?.name || option}
                      >
                        {option?.name || option}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
              {localFormValues?.documentType === "Other" && (
                <div className="flex-row">
                  <TextField
                    key={"input-other-document-type"}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    placeholder="Provide other document type"
                    autoComplete="new-password"
                    variant="outlined"
                    name="otherDocumentType"
                    id="input-other-document-type"
                    label="Document Type"
                    onChange={handleLocalChange}
                    value={localFormValues?.otherDocumentType || ""}
                  />
                  <div
                    onClick={() => {
                      setLocalFormValues({
                        ...localFormValues,
                        documentType: "-- Select One --",
                        otherDocumentType: null,
                      });
                    }}
                    style={{
                      height: "35px",
                      width: "35px",
                      borderTop: "1px solid #d1d1d1",
                      borderBottom: "1px solid #d1d1d1",
                      borderRight: "1px solid #d1d1d1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      // borderRadius: "50%",
                      // marginLeft: "3px",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                    }}
                  >
                    x
                  </div>
                </div>
              )}
            </Grid>
            <Grid item md={5} xs={12} className="padded-grid-item">
              <FileInput
                onChange={handleFileChange}
                file={localFormValues?.attachment}
                disabled={
                  isEmpty(localFormValues?.documentType) ||
                  localFormValues?.documentType === "-- Select One --"
                }
                name="attachment"
                fileType={
                  attachmentTypes?.find(
                    (x) => x?.name === localFormValues?.documentType
                  )?.extension
                }
                label="Document"
              />
            </Grid>
            <Grid item md={2} xs={12} className="padded-grid-item">
              <Button
                className="btn-login"
                variant="contained"
                onClick={handleUpload}
                margin="dense"
                size="medium"
                color="secondary"
                disabled={
                  isUploading() ||
                  isEmpty(localFormValues?.documentType) ||
                  isEmpty(localFormValues?.attachment) ||
                  (localFormValues?.documentType === "Other" &&
                    isEmpty(localFormValues?.otherDocumentType))
                }
                style={{ textTransform: "unset" }}
              >
                {isUploading() && (
                  <CircularProgress
                    size={20}
                    color="primary"
                    style={{ marginRight: "8px" }}
                  />
                )}
                Upload
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const BuildTable = () => {
    return (
      <>
        {statusGet === "idle" && list?.length > 0 && (
          <table className="mt-10 table-attachments">
            <thead>
              <th>S/N</th>
              <th>Attachment</th>
              <th>Date Added</th>
              <th></th>
              {!readOnly && <th></th>}
            </thead>
            <tbody>
              {list?.map((attachment, index) => (
                <tr>
                  <td className="text-align-center td-sn">{index + 1}</td>
                  <td>{attachment?.TYPE}</td>
                  <td>{moment(attachment?.DATE_ADDED).format(dateFormat)}</td>
                  <td className="td-btn">
                    <button
                      disabled={isUpdating()}
                      onClick={() => {
                        setIsOpenAttachmentView(true);
                        setSelectedAttachment(attachment);
                      }}
                    >
                      View
                    </button>
                  </td>
                  {!readOnly && (
                    <td className="td-btn">
                      <button
                        disabled={isUpdating() || attachment?.LOCKED === "Y"}
                        onClick={() => {
                          setSelectedAttachment(attachment);
                          setIsOpenConfirm(true);
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {statusGet === "loading" && (
          <LoadingContainer
            hasBackground={false}
            hasFixedHeight={false}
            message="Loading attachments..."
          />
        )}

        {errorGet !== null && statusGet !== "loading" && (
          <ErrorContainer
            message={errorGet?.message}
            onReload={() => {
              loadAttachments();
            }}
            hasBackground={false}
            hasFixedHeight={false}
          />
        )}

        {list?.length === 0 && statusGet === "idle" && (
          <div
            style={{ color: "#999" }}
            className="mt-30 mb-30 flex-row align-items-center justify-content-center"
          >
            No Attachments added
          </div>
        )}

        {isOpenAttachmentView && selectedAttachment && (
          <ViewAttachmentDialog
            open={isOpenAttachmentView}
            attachment={selectedAttachment}
            handleClose={() => {
              setIsOpenAttachmentView(false);
              setSelectedAttachment(null);
            }}
          />
        )}

        {isOpenConfirm && selectedAttachment && (
          <ConfirmationDialog
            open={isOpenConfirm}
            message={`Are you sure you want to remove the attachment "${selectedAttachment?.TYPE}"`}
            title="Confirm Remove"
            handleResponse={() => {
              handleRemoveAttachment();
            }}
            onClose={() => {
              setSelectedAttachment(null);
              setIsOpenConfirm(false);
            }}
          />
        )}
      </>
    );
  };

  return (
    <div className="mt-10">
      {BuildForm()}
      {BuildTable()}
    </div>
  );
};

export default AttachmentsContainer;
